import dayjs from 'dayjs';
import { z } from 'zod';

import {
	CHARTER_STATUSES,
	CharterCustomer,
	CharterScheduleItem,
} from 'Modules/Charter/Types/CharterScheduleItem';

/**
 * How the two ends are held while the form is open: local wall-clock text, the same thing the
 * picker shows and one `:00` short of what goes over the wire.
 *
 * Text rather than a `Date` because the picker reads and writes text, and because a charter is
 * agreed in wall-clock — 9am on the day, whatever the browser thinks its offset is.
 */
export const CHARTER_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

/** Shown as soon as both ends are known rather than on save, so it lands while the picker is in mind. */
export const END_BEFORE_START = 'The charter must end after it starts';

/**
 * What a booking needs that a reserve doesn't. Both say what is missing and why it is being asked
 * for, because the reader pressed a button that looked like it should have worked.
 */
export const BOOKING_NEEDS_CUSTOMER = 'Pick the customer this charter is booked for';
export const BOOKING_NEEDS_PRICE = 'Set the price this charter is booked at';

/**
 * A charter as the create form holds it, before it is one.
 *
 * The start and the end are each a date and a time in one field, rather than one date with two times
 * under it: a charter can be handed back the following morning, and a shared date would have made
 * that unsayable.
 *
 * Optional text fields are empty strings rather than undefined, because an input bound to undefined
 * renders as an uncontrolled one.
 *
 * Everything a charter can be got wrong on is here except the clash rules, which can't be: they are
 * judged against the ferry's existing work rather than against the form, and the form only has the
 * week the schedule has loaded to judge them by. Those live in `findCharterClash`.
 */
export const charterFormSchema = z
	.object({
		ferryId: z.string().min(1, 'Select a ferry'),
		startDateTime: z.string().min(1, 'Select when the charter starts'),
		endDateTime: z.string().min(1, 'Select when the charter ends'),
		name: z.string(),

		/**
		 * The whole customer rather than just an id: the form is the only thing holding the pick, so it
		 * has to hold enough of the customer to name them and to know which dynamic fields they are
		 * asked, without going back for the record.
		 */
		customer: z.custom<CharterCustomer>().nullable(),
		price: z.number().optional(),
		notes: z.string(),

		/**
		 * Answers to the dynamic fields aimed at charters, keyed by field id, every answer a string —
		 * the same shape the booking wizard holds its own in, so one renderer serves both.
		 *
		 * Which fields are asked, and which of them are required, is not in this schema: it is
		 * configured rather than declared, and only the field definitions know. Those checks run on
		 * submit beside the clash check, in `CharterCrud`.
		 */
		dynamicFields: z.record(z.string()),

		/**
		 * Which button was pressed, not a field anyone fills in: the action bar writes it immediately
		 * before submitting. It is in the schema rather than beside it so the rules that only apply to
		 * a booking are checked in the same pass as everything else, and land on the fields they are
		 * about.
		 */
		status: z.enum(CHARTER_STATUSES),
	})
	.superRefine((values, ctx) => {
		const block = charterBlock(values);

		// A reserve holds the ferry and asks for nothing else. A booking is a commitment, so it has
		// to say who by and for how much — the server refuses one without either.
		if (values.status === 'Booked') {
			if (!values.customer) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['customer'],
					message: BOOKING_NEEDS_CUSTOMER,
				});
			}

			if (values.price == null) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['price'],
					message: BOOKING_NEEDS_PRICE,
				});
			}
		}

		// Later date as well as later time: a charter handed back the next morning is a normal one.
		if (block && block.end <= block.start) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['endDateTime'],
				message: END_BEFORE_START,
			});
		}
	});

export type CharterFormValues = z.infer<typeof charterFormSchema>;

/** The form's id, so the Save button can sit outside the `<form>` and still submit it. */
export const CHARTER_FORM_ID = 'charter-crud-form';

/**
 * A blank charter. Both ends start empty — see `CharterCrud` for what the pickers open on instead.
 *
 * Opens as a reserve: the state that commits to nothing is the safe one to default to, and the
 * action bar overwrites it the moment anyone presses the other button.
 */
export function emptyCharterForm(): CharterFormValues {
	return {
		ferryId: '',
		startDateTime: '',
		endDateTime: '',
		name: '',
		customer: null,
		notes: '',
		dynamicFields: {},
		status: 'Reserved',
	};
}

/**
 * A saved charter as the form holds it, for the sidebar opened on an existing one.
 *
 * The two ends come back as `Date`s and go into the pickers as the wall-clock text they read and
 * write. The optional text fields become empty strings for the same reason `emptyCharterForm` uses
 * them: an input bound to undefined renders as an uncontrolled one.
 */
export function charterFormValues(charter: CharterScheduleItem): CharterFormValues {
	return {
		ferryId: charter.ferry.id,
		startDateTime: dayjs(charter.startDateTime).format(CHARTER_DATE_TIME_FORMAT),
		endDateTime: dayjs(charter.endDateTime).format(CHARTER_DATE_TIME_FORMAT),
		name: charter.name ?? '',
		customer: charter.customer ?? null,
		price: charter.price ?? undefined,
		notes: charter.notes ?? '',
		dynamicFields: charterDynamicFieldValues(charter),
		status: charter.status,
	};
}

/**
 * A saved charter's answers as the form holds them — keyed by field id, so an input can find its own
 * answer without searching the list on every render.
 */
export function charterDynamicFieldValues(charter: CharterScheduleItem): Record<string, string> {
	return Object.fromEntries((charter.dynamicFields ?? []).map(answer => [answer.fieldId, answer.value ?? '']));
}

/**
 * The block the charter holds the ferry for, or undefined until both ends are set and readable.
 *
 * The clash check, the duration line and the saved charter all measure the same span, so they read
 * it from here rather than each assembling it their own way.
 */
export function charterBlock(values: {
	startDateTime?: string;
	endDateTime?: string;
}): { start: Date; end: Date } | undefined {
	if (!values.startDateTime || !values.endDateTime) {
		return undefined;
	}

	const start = dayjs(values.startDateTime);
	const end = dayjs(values.endDateTime);

	if (!start.isValid() || !end.isValid()) {
		return undefined;
	}

	return { start: start.toDate(), end: end.toDate() };
}
